// 별 모으기 수집 상태 — 이 기기에만 저장(localStorage, 비회원·무수집 불변식 준수).
// 형태: { [zone]: ["s3_4", ...] }. 손상 시 빈 상태로 폴백(절대 throw 안 함).

import { STAR_ZONES, STARS_PER_ZONE } from "../world/zoneMeta";

const KEY = "gc_stars_v1";

type StarStore = Record<string, string[]>;

function read(): StarStore {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const o = JSON.parse(raw) as unknown;
    if (!o || typeof o !== "object") return {};
    const out: StarStore = {};
    for (const [z, v] of Object.entries(o as Record<string, unknown>)) {
      if (Array.isArray(v)) out[z] = v.filter((s): s is string => typeof s === "string");
    }
    return out;
  } catch {
    return {};
  }
}

export function getCollectedStars(zone: string): string[] {
  return read()[zone] ?? [];
}

/**
 * 🔴 **셀 때는 한 존의 총합을 넘지 않는다.**
 *
 * 별 id 는 맵 좌표에서 나온다. 운영자가 지도를 고치거나(오브젝트 이동) 배치 규칙이 바뀌면
 * 예전 id 가 저장소에 남는다. 그걸 그대로 세면 미션 패널이 **「⭐ 4/3」** 를 띄우고,
 * 전체 막대가 100%를 넘고, 다 안 모았는데 **「다 모았어요」** 가 뜬다.
 *
 * `pruneStars` 가 바로잡지만 그건 **그 존에 들어갔을 때만** 돈다 — 미션 패널과 마을 이야기는
 * 안 들어간 존까지 센다. 그래서 세는 자리에서 한 번 더 막는다.
 *
 * (남은 id 개수가 총합과 같으면 여기서는 못 가른다 — 그건 그 존에 들어가면 바로잡힌다.)
 */
export function collectedCount(zone: string): number {
  return Math.min(getCollectedStars(zone).length, STARS_PER_ZONE);
}

export function addCollectedStar(zone: string, id: string): void {
  try {
    const s = read();
    const list = s[zone] ?? [];
    if (!list.includes(id)) list.push(id);
    s[zone] = list;
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* 프라이빗 모드 등 저장 실패 — 세션 내 수집만 유지 */
  }
}

/**
 * 지금 지도에 **실제로 있는 별**만 남긴다 — 저장분과 현재 배치를 맞춘다.
 *
 * 별 id 는 맵 좌표(`s{x}_{y}`)에서 나온다. 그래서 운영자가 지도를 고치면(오브젝트 이동·
 * 크기 변경) 배치가 달라져 예전 id 가 저장소에 남고, "⭐ 4/3" 처럼 총합을 넘는 숫자가 뜬다.
 * 남은 id 를 지워 표시와 저장을 함께 바로잡는다. 반환값은 유효한 수집 수.
 */
export function pruneStars(zone: string, valid: Iterable<string>): number {
  const keep = new Set(valid);
  const kept = getCollectedStars(zone).filter((id) => keep.has(id));
  try {
    const s = read();
    s[zone] = kept;
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* 저장 실패는 무해 — 이번 세션 표시만 바로잡힌다 */
  }
  return kept.length;
}

/**
 * 전체 수집 수(HUD·마을 이야기용).
 *
 * 🔴 **존마다 상한을 걸어 더한다.** 그냥 더하면 저장소에 남은 옛 id 까지 세어
 * 「모은 별」이 있을 수 있는 수를 넘는다. 모르는 존(지도에서 빠진 것)은 세지 않는다.
 */
export function totalCollectedStars(): number {
  return STAR_ZONES.reduce((a, z) => a + collectedCount(z), 0);
}
